import { rgbStringToHex } from "utils/color/rgbHelpers";

import { buildColorLockKey } from "components/filter/util/colorLockKey";

// The normalized key a blank/whitespace-only value collapses to. Shared by
// bin exclusion below and by rigFracColorAssignment's blank-value handling,
// so both sides agree on what counts as "no value" without either
// re-deriving the check.
export const NA_MATCH_KEY = "n/a";

// Paints both a blank-operator rig on the map and the chart's matching "n/a"
// series. The one shared n/a colour — do not add a second one, and do not
// try to reproduce chart-svc's own grey, since the chart is client-coloured
// after this.
export const RIG_FRAC_NA_COLOR = "#a2aaad";

/**
 * Trim + lowercase a value for matching. Blank or whitespace-only collapses
 * to the shared n/a key. Nothing else — no suffix stripping, no punctuation
 * removal (FR-5: exact match after trim and case-fold, no other
 * normalization).
 *
 * Coerces with `String(...)` before trimming: the well legend isn't
 * runtime-validated on its way into the atom this reads from, so a value
 * that's typed as `string` but isn't one at runtime would otherwise throw
 * straight out of `.trim()` — and since this runs for every chart's legend,
 * that would take down charts and the map together, not just the rig/frac
 * ones.
 */
export function normalizeMatchKey(value: Maybe<string>): string {
  const trimmed = value == null ? "" : String(value).trim();
  return trimmed ? trimmed.toLowerCase() : NA_MATCH_KEY;
}

const OTHERS_LABEL_PATTERN = /^\d+ others$/;
const SAMPLE_SIZE_LABEL_PATTERN = /^less than (\d+|n) sample size$/;

/** True for a focus Others bin ("3 Others"), in any case. */
export function isOthersBinLabel(value: Maybe<string>): boolean {
  return OTHERS_LABEL_PATTERN.test(normalizeMatchKey(value));
}

/**
 * True for rollup/placeholder legend entries ("3 Others", "n/a", "Less Than
 * 10 Sample Size") — these are not real operator or basin names and must
 * never be matched against a rig or frac value (FR-6).
 */
export function isExcludedBinLabel(value: Maybe<string>): boolean {
  const key = normalizeMatchKey(value);
  return (
    key === NA_MATCH_KEY ||
    OTHERS_LABEL_PATTERN.test(key) ||
    SAMPLE_SIZE_LABEL_PATTERN.test(key)
  );
}

/**
 * Trim + lowercase a colour for comparison, converting an `rgb(...)` string
 * to hex first so a legend colour can be compared against a generated
 * palette colour (always lowercase hex). `rgbStringToHex` throws on a
 * malformed input, so a bad legend colour falls back to its trimmed,
 * lowercased original rather than breaking colour resolution.
 */
export function normalizeColorKey(color: Maybe<string>): string {
  const trimmed = color == null ? "" : String(color).trim().toLowerCase();
  if (!trimmed) {
    return trimmed;
  }
  try {
    return rgbStringToHex(trimmed).toLowerCase();
  } catch {
    return trimmed;
  }
}

/**
 * Builds the same key state.app.lockedColors is keyed on (see
 * store/features/app/appSlice.ts and components/filter/util/colorLockKey.ts),
 * so a bin colour lock or preferred colour is looked up through the one
 * existing keyed colour space rather than a second, parallel one. `value` is
 * the raw legend value (post-removeCount), not the normalized match key —
 * locks are stored case-sensitively against the exact legend value.
 *
 * Delegates to `buildColorLockKey` so quantile bins collapse to their stable
 * ordinal token here too (EVA-90). Keying this on the raw value instead would
 * miss every quantile lock, since the store holds only the ordinal key.
 */
export function buildLockedColorKey(property: string, value: string): string {
  return buildColorLockKey(property, value);
}
